/**
 * useArchivio — F-006 Phase 5 (IMPL-P5-003).
 *
 * Context + Provider + hook for the archivio list page. Pairs a TanStack Query
 * `useInfiniteQuery` (paginated FTS results) with a separate lightweight
 * `useQuery` for `hasAnyPratiche` so the UI can reliably distinguish:
 *
 *  - status === 'empty'      → user has zero pratiche overall   (EmptyArchivio)
 *  - status === 'no-results' → user has pratiche but the filters give zero hits
 *                              (NoResultsState — keeps filters visible)
 *
 * URL is the source of truth for `q` + filters via `useSearchParams` so the
 * archivio is deep-linkable and reload-stable. Changing the search/filters
 * implicitly changes the query key → TanStack refetches with the new params.
 *
 * Mirror of `useBillingProfile` shape: explicit status machine + hook that
 * throws if used outside the Provider.
 */
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { searchPratiche } from '../lib/archivio-client';
import type { ArchivioListQuery, ArchivioListResponse, PraticaListItem } from '@app/shared';

export type ArchivioStatus = 'loading' | 'empty' | 'no-results' | 'ready' | 'error';

/** Filter keys reflected in the URL search params. */
const FILTER_KEYS = ['tipologia', 'stato', 'parte', 'dateFrom', 'dateTo'] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

const QUERY_PARAM_KEYS = ['q', ...FILTER_KEYS] as const;

export interface ArchivioState {
  status: ArchivioStatus;
  items: PraticaListItem[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  searchTerm: string;
  setSearchTerm: (q: string) => void;
  filters: Partial<Record<FilterKey, string>>;
  setFilter: (key: FilterKey, value: string | undefined) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  refetch: () => void;
}

const ArchivioContext = createContext<ArchivioState | null>(null);

export function ArchivioProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchTerm = searchParams.get('q') ?? '';

  // Stable filter object (memoized so query keys don't change identity on
  // unrelated rerenders).
  const filters = useMemo<Partial<Record<FilterKey, string>>>(() => {
    const out: Partial<Record<FilterKey, string>> = {};
    for (const k of FILTER_KEYS) {
      const v = searchParams.get(k);
      if (v) out[k] = v;
    }
    return out;
  }, [searchParams]);

  const hasActiveFilters = Object.keys(filters).length > 0 || searchTerm.length > 0;

  const setSearchTerm = useCallback(
    (q: string) => {
      setSearchParams(
        (prev) => {
          if (q) prev.set('q', q);
          else prev.delete('q');
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setFilter = useCallback(
    (key: FilterKey, value: string | undefined) => {
      setSearchParams(
        (prev) => {
          if (value) prev.set(key, value);
          else prev.delete(key);
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        for (const k of QUERY_PARAM_KEYS) prev.delete(k);
        return prev;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // Effective query passed to the API (skip empty values).
  // `parte` is narrowed by ArchivioListQuerySchema to a literal union; the URL
  // can technically hold any string, so we cast through Partial<ArchivioListQuery>.
  const apiQuery = useMemo<Partial<ArchivioListQuery>>(
    () => ({
      ...(searchTerm ? { q: searchTerm } : {}),
      ...(filters.tipologia ? { tipologia: filters.tipologia } : {}),
      ...(filters.stato ? { stato: filters.stato } : {}),
      ...(filters.parte === 'proprietario' || filters.parte === 'inquilino'
        ? { parte: filters.parte }
        : {}),
      ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
      ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
    }),
    [searchTerm, filters],
  );

  const infiniteQuery = useInfiniteQuery({
    queryKey: ['archivio', 'list', apiQuery],
    queryFn: ({ pageParam }) =>
      searchPratiche({ ...apiQuery, cursor: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: ArchivioListResponse) => last.nextCursor ?? undefined,
    // staleTime inherits the QueryClient default (30s) — see lib/query-client.ts.
  });

  const items = useMemo(
    () => infiniteQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [infiniteQuery.data],
  );

  const hasAnyPraticheFromApi = infiniteQuery.data?.pages.at(-1)?.hasAnyPratiche;

  // Separate lightweight query: only fires when the main list returned zero
  // items AND the server didn't already tell us `hasAnyPratiche`. Stale for
  // 1 minute (slow-changing). Disabled while the main query is fetching to
  // avoid double round-trips during initial mount.
  const hasAnyQuery = useQuery({
    queryKey: ['archivio', 'has-any'],
    queryFn: () => searchPratiche({ take: 1 }),
    enabled:
      items.length === 0 &&
      hasAnyPraticheFromApi === undefined &&
      !infiniteQuery.isFetching &&
      !infiniteQuery.isError,
    staleTime: 60_000,
    select: (data) => data.hasAnyPratiche ?? data.items.length > 0,
  });

  // Status machine — precedence matters.
  // TODO(SIM-023): The transition `loading → ready → loading` can still flash
  // briefly when `infiniteQuery.isLoading` flips to false but `hasAnyQuery` is
  // still resolving on an empty result. We guard against the most common case
  // (no items + side query pending) by using `isFetching` only on the initial
  // load — refetches do NOT downgrade an already-ready list back to loading.
  let status: ArchivioStatus;
  if (infiniteQuery.isError) {
    status = 'error';
  } else if (infiniteQuery.isLoading) {
    status = 'loading';
  } else if (items.length > 0) {
    status = 'ready';
  } else {
    // items.length === 0 — distinguish empty (no pratiche ever) from no-results.
    const hasAny =
      hasAnyPraticheFromApi !== undefined ? hasAnyPraticheFromApi : hasAnyQuery.data;
    if (hasAny === false) status = 'empty';
    else if (hasAny === true) status = 'no-results';
    else status = 'loading'; // still resolving the side query
  }

  const value: ArchivioState = {
    status,
    items,
    fetchNextPage: () => {
      if (infiniteQuery.hasNextPage && !infiniteQuery.isFetchingNextPage) {
        void infiniteQuery.fetchNextPage();
      }
    },
    hasNextPage: !!infiniteQuery.hasNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    isLoading: infiniteQuery.isLoading,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
    refetch: () => {
      void infiniteQuery.refetch();
    },
  };

  return <ArchivioContext.Provider value={value}>{children}</ArchivioContext.Provider>;
}

export function useArchivio(): ArchivioState {
  const ctx = useContext(ArchivioContext);
  if (!ctx) {
    throw new Error('useArchivio must be used inside <ArchivioProvider>');
  }
  return ctx;
}

/**
 * Safe variant of `useArchivio()` that returns `null` instead of throwing when
 * used outside `<ArchivioProvider>`. Used by components that support a dual
 * controlled/provider-driven API (e.g. `ArchivioSearchBar`) so the hook can be
 * called unconditionally (Rules of Hooks) even when the consumer is rendered
 * standalone in tests.
 */
export function useArchivioOptional(): ArchivioState | null {
  return useContext(ArchivioContext);
}
